/* =========================================================================
   ClueVine — Branches (word chain)
   Each word must begin with the previous word's last letter (garden → nettle →
   elm …). Grow the chain to the target length. You can bank a shorter chain any
   time, so a dead-end is never a loss — and hints always surface a valid next
   word. Endless-friendly and fast.
   ========================================================================= */

import { pickAvoiding } from '../../engine/seed';
import type {
  Dictionary,
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export interface BranchesConfig {
  minLength: number;
  target: number;
}

export interface BranchesState {
  chain: string[]; // chain[0] is the seed
  current: string;
  minLength: number;
  target: number;
  finished: boolean;
}

export type BranchesMove =
  | { type: 'add'; ch: string }
  | { type: 'remove' }
  | { type: 'submit' }
  | { type: 'finish' };

export const BRANCHES_DIFFICULTIES: DifficultyLevel<BranchesConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '3+ letters · chain of 7', scoreMultiplier: 0.9, config: { minLength: 3, target: 7 } },
  { id: 'standard', label: 'Garden', blurb: '4+ letters · chain of 8', scoreMultiplier: 1, config: { minLength: 4, target: 8 } },
  { id: 'hard', label: 'Thicket', blurb: '4+ letters · chain of 10', scoreMultiplier: 1.3, config: { minLength: 4, target: 10 } },
];

export function requiredLetter(state: BranchesState): string {
  const last = state.chain[state.chain.length - 1];
  return last[last.length - 1];
}

/** First unused valid word starting with `letter`, scanning shortest-first. */
export function findNextWord(
  dict: Dictionary,
  letter: string,
  minLength: number,
  used: readonly string[],
  maxLength = 8,
): string | null {
  const usedSet = new Set(used);
  for (let len = minLength; len <= maxLength; len++) {
    for (const w of dict.byLength(len)) {
      if (w[0] === letter && !usedSet.has(w)) return w;
    }
  }
  return null;
}

export function generate(ctx: GenerateContext<BranchesConfig>): BranchesState {
  const { minLength, target } = ctx.difficulty.config;
  const seedPool = ctx.dictionary.answersByLength(5);
  const seed = pickAvoiding(ctx.prng, seedPool, ctx.avoid);
  return { chain: [seed], current: '', minLength, target, finished: false };
}

export function status(state: BranchesState): PuzzleStatus {
  if (state.chain.length >= state.target) return 'won';
  if (state.finished) return state.chain.length >= 2 ? 'won' : 'lost';
  return 'playing';
}

export function validateMove(state: BranchesState, move: BranchesMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  const word = state.current;
  if (word.length < state.minLength) return { ok: false, reason: `At least ${state.minLength} letters` };
  if (word[0] !== requiredLetter(state)) {
    return { ok: false, reason: `Must start with “${requiredLetter(state).toUpperCase()}”` };
  }
  if (state.chain.includes(word)) return { ok: false, reason: 'Already used' };
  // dictionary validity checked in the mode wrapper
  return { ok: true };
}

export function reduce(state: BranchesState, move: BranchesMove): BranchesState {
  switch (move.type) {
    case 'add':
      if (state.current.length >= 14) return state;
      return { ...state, current: state.current + move.ch };
    case 'remove':
      return state.current ? { ...state, current: state.current.slice(0, -1) } : state;
    case 'submit': {
      const word = state.current;
      if (word.length < state.minLength || word[0] !== requiredLetter(state) || state.chain.includes(word)) {
        return state;
      }
      return { ...state, chain: [...state.chain, word], current: '' };
    }
    case 'finish':
      return { ...state, finished: true };
    default:
      return state;
  }
}

export function handleKey(state: BranchesState, key: InputKey): BranchesMove | null {
  if (key.type === 'char') {
    return state.current.length < 14 ? { type: 'add', ch: key.value } : null;
  }
  if (key.type === 'backspace') {
    return state.current ? { type: 'remove' } : null;
  }
  return { type: 'submit' };
}

export function makeHint(state: BranchesState, dict: Dictionary): HintOutcome<BranchesState> | null {
  if (status(state) !== 'playing') return null;
  const suggestion = findNextWord(dict, requiredLetter(state), state.minLength, state.chain);
  if (!suggestion) return null;
  return { message: `Try “${suggestion.toUpperCase()}”.`, penalty: 40 };
}

export function score(state: BranchesState): ModeScore {
  if (status(state) !== 'won') {
    return { base: 0, lines: [{ label: 'No chain', value: 0, kind: 'base' }], parMs: state.target * 8000 };
  }
  const links = state.chain.length - 1;
  const base = links * 120;
  return {
    base,
    lines: [{ label: `Chain of ${state.chain.length}`, value: base, kind: 'base' }],
    parMs: state.target * 8000,
    perfect: state.chain.length >= state.target,
  };
}

export function summarize(state: BranchesState): ResultSummary {
  const won = status(state) === 'won';
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `Chain of ${state.chain.length}` : 'No chain made',
    answer: state.chain.map((w) => w.toUpperCase()).join(' → '),
    revealLabel: 'Chain',
    avoidKeys: [state.chain[0]],
    shareGrid: [won ? `🔗 chain of ${state.chain.length}` : '🥀 broke early'],
  };
}

export function deserialize(raw: unknown): BranchesState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<BranchesState>;
  if (!Array.isArray(s.chain) || typeof s.current !== 'string') return null;
  if (typeof s.minLength !== 'number' || typeof s.target !== 'number') return null;
  return {
    chain: s.chain.filter((w): w is string => typeof w === 'string'),
    current: s.current,
    minLength: s.minLength,
    target: s.target,
    finished: Boolean(s.finished),
  };
}
